import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  CircularProgress,
  Chip,
  Button,
  Tooltip,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Avatar,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsNone as NotificationsNoneIcon,
  DoneAll as DoneAllIcon,
  NotificationsActive as NotificationsActiveIcon,
  Article as ArticleIcon,
  Close as CloseIcon,
  SignalCellularOff as SignalCellularOffIcon,
  CalendarToday as CalendarTodayIcon,
  EventBusy as EventBusyIcon,
  Alarm as AlarmIcon,
  SupportAgent as SupportAgentIcon,
  Campaign as CampaignIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import { formatRelativeTime } from '../utils/formatters';
import { notificationsService } from '../services/notificationsService';
import { useNotifications } from '../hooks/useNotifications';
import { usePushNotifications } from '../hooks/usePushNotifications';
import NotificationDetailModal from './NotificationDetailModal';
import styles from './NotificationCenter.module.css';

const PUSH_DENIED_DISMISSED_KEY = 'agendario_push_denied_dismissed';

// Mapa de tipos de notificação para ícone, cor e label
const NOTIFICATION_TYPE_MAP = {
  'appointment.created': {
    icon: CalendarTodayIcon,
    color: '#4CAF50',
    bgColor: 'rgba(76, 175, 80, 0.1)',
    label: 'Agendamento',
  },
  'appointment.cancelled': {
    icon: EventBusyIcon,
    color: '#f44336',
    bgColor: 'rgba(244, 67, 54, 0.1)',
    label: 'Cancelamento',
  },
  'appointment.reminder': {
    icon: AlarmIcon,
    color: '#FF9800',
    bgColor: 'rgba(255, 152, 0, 0.1)',
    label: 'Lembrete',
  },
  'support.reply.created': {
    icon: SupportAgentIcon,
    color: '#2196F3',
    bgColor: 'rgba(33, 150, 243, 0.1)',
    label: 'Suporte',
  },
  'admin.broadcast': {
    icon: CampaignIcon,
    color: '#9C27B0',
    bgColor: 'rgba(156, 39, 176, 0.1)',
    label: 'Comunicado',
  },
  'admin.info': {
    icon: InfoIcon,
    color: '#2196F3',
    bgColor: 'rgba(33, 150, 243, 0.1)',
    label: 'Informação',
  },
  'admin.alert': {
    icon: WarningIcon,
    color: '#f44336',
    bgColor: 'rgba(244, 67, 54, 0.1)',
    label: 'Alerta',
  },
  'admin.maintenance': {
    icon: BuildIcon,
    color: '#757575',
    bgColor: 'rgba(117, 117, 117, 0.1)',
    label: 'Manutenção',
  },
};

const getNotificationTypeInfo = (type) => {
  return NOTIFICATION_TYPE_MAP[type] || {
    icon: NotificationsIcon,
    color: '#757575',
    bgColor: 'rgba(117, 117, 117, 0.1)',
    label: 'Notificação',
  };
};

/**
 * NotificationCenter - Sistema de notificacoes isolado
 * Funciona independente do resto do app, nunca quebra a UI
 * @param {Object} props
 * @param {boolean} props.autoOpen - Se true, abre o popover automaticamente ao montar
 * @param {function} props.onAutoOpened - Callback chamado apos abrir automaticamente
 */
export default function NotificationCenter({ autoOpen = false, onAutoOpened, onToast }) {
  const navigate = useNavigate();
  const buttonRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [autoOpenHandled, setAutoOpenHandled] = useState(false);
  const [deniedDismissed, setDeniedDismissed] = useState(
    () => localStorage.getItem(PUSH_DENIED_DISMISSED_KEY) === 'true'
  );
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailNotificationId, setDetailNotificationId] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all' | 'unread'
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const observerRef = useRef(null);

  // Push notifications hook
  const {
    pushSupported,
    permissionState,
    isLoading: pushLoading,
    requestPermission,
  } = usePushNotifications();

  // Carregar contador inicial
  useEffect(() => {
    loadUnreadCount();
  }, []);

  // Auto open se prop autoOpen for true (deep-link de email)
  useEffect(() => {
    if (autoOpen && !autoOpenHandled && buttonRef.current) {
      const timer = setTimeout(() => {
        setAnchorEl(buttonRef.current);
        loadNotifications();
        setAutoOpenHandled(true);
        if (onAutoOpened) {
          onAutoOpened();
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [autoOpen, autoOpenHandled, onAutoOpened]);

  // IntersectionObserver para infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMoreNotifications();
        }
      },
      { threshold: 0.1 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current);
      }
    };
  }, [hasMore, loadingMore, notifications.length]);

  // Conectar ao SignalR
  const handleNewNotification = useCallback((notification) => {
    console.log('[NotificationCenter] New notification received:', notification);

    // Adicionar nova notificacao no topo
    setNotifications((prev) => [notification, ...prev]);

    // Incrementar contador
    setUnreadCount((prev) => prev + 1);

    // Exibir toast notification
    if (onToast) {
      onToast(notification);
    }

    // Notificacao OS-level e responsabilidade do Service Worker via Push.
    // Nao chamar new Notification() aqui para evitar duplicacao.
  }, [onToast]);

  const { isConnected } = useNotifications(handleNewNotification);

  const loadUnreadCount = async () => {
    try {
      const count = await notificationsService.getUnreadCount();
      setUnreadCount(count);
      setHasError(false);
    } catch {
      console.warn('[NotificationCenter] Error loading unread count');
      setHasError(true);
    }
  };

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const data = await notificationsService.getNotifications(0, 20);
      setNotifications(data);
      setPage(0);
      setHasMore(data.length === 20);
      setHasError(false);
    } catch {
      console.warn('[NotificationCenter] Error loading notifications');
      setHasError(true);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreNotifications = async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      const skip = notifications.length;
      const data = await notificationsService.getNotifications(skip, 20);

      if (data.length > 0) {
        setNotifications((prev) => [...prev, ...data]);
        setPage((prev) => prev + 1);

        // Se retornou menos de 20, chegou ao fim
        if (data.length < 20) {
          setHasMore(false);
        }
      } else {
        setHasMore(false);
      }

      setHasError(false);
    } catch {
      console.warn('[NotificationCenter] Error loading more notifications');
      setHasError(true);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleMarkAsRead = async (notificationId, event) => {
    event.stopPropagation();

    try {
      const success = await notificationsService.markAsRead(notificationId);

      if (success) {
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notificationId ? { ...n, readAt: new Date().toISOString() } : n
          )
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch {
      console.warn('[NotificationCenter] Error marking as read');
    }
  };

  const handleMarkAllAsRead = async () => {
    const unreadNotifications = notifications.filter((n) => !n.readAt);

    if (unreadNotifications.length === 0) return;

    try {
      // Marcar todas como lidas em paralelo (otimista)
      await Promise.all(unreadNotifications.map((n) => notificationsService.markAsRead(n.id)));

      // Atualizar estado local
      setNotifications((prev) =>
        prev.map((n) => ({
          ...n,
          readAt: n.readAt ? n.readAt : new Date().toISOString(),
        }))
      );

      // Zerar contador
      setUnreadCount(0);
    } catch {
      console.warn('[NotificationCenter] Error marking all as read');
    }
  };

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    // Reset paginação
    setPage(0);
    setHasMore(true);
    // Carregar notificacoes e contar não-lidas em paralelo
    Promise.all([loadNotifications(), loadUnreadCount()]);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.readAt) {
      try {
        await notificationsService.markAsRead(notification.id);
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notification.id ? { ...n, readAt: new Date().toISOString() } : n
          )
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch {
        console.warn('[NotificationCenter] Error marking as read on click');
      }
    }

    // If notification has rich body content, open detail modal (body is fetched on-demand via API)
    if (notification.hasBody) {
      handleClose();
      setDetailNotificationId(notification.id);
      setDetailModalOpen(true);
      return;
    }

    const actionUrl = notification.actionUrl || notification.data?.actionUrl;
    if (actionUrl) {
      handleClose();
      if (actionUrl.startsWith('/')) {
        navigate(actionUrl);
      } else {
        window.open(actionUrl, '_blank');
      }
    }
  };

  const handleActivatePush = async () => {
    await requestPermission();
  };

  const handleDismissDenied = () => {
    localStorage.setItem(PUSH_DENIED_DISMISSED_KEY, 'true');
    setDeniedDismissed(true);
  };

  const formatDate = formatRelativeTime;

  const open = Boolean(anchorEl);

  // Determinar se deve exibir banner de push
  const showDefaultBanner = pushSupported && permissionState === 'default';
  const showDeniedBanner = pushSupported && permissionState === 'denied' && !deniedDismissed;

  if (hasError) {
    return (
      <Tooltip title="Notificacoes temporariamente indisponiveis">
        <IconButton color="inherit" disabled>
          <NotificationsNoneIcon />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip title={isConnected ? 'Notificacoes (conectado)' : 'Notificacoes'}>
        <IconButton ref={buttonRef} color="inherit" onClick={handleOpen}>
          <Badge
            badgeContent={unreadCount}
            color="error"
            className={unreadCount > 0 ? styles.badgePulse : ''}
          >
            {unreadCount > 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: `min(420px, 95vw)`,
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            mt: 1,
          },
        }}
      >
        {/* Header fixo */}
        <Box
          sx={{
            p: 2,
            bgcolor: 'primary.main',
            color: 'white',
            flexShrink: 0,
            borderBottom: '1px solid',
            borderColor: 'primary.dark',
          }}
        >
          {/* Linha 1: Título + Fechar */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" fontWeight="bold">
              Notificacoes
            </Typography>
            <Tooltip title="Fechar">
              <IconButton
                size="small"
                onClick={handleClose}
                sx={{
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Linha 2: Chip + Ações */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
            {/* Chip Ao vivo / Offline */}
            <Chip
              icon={
                isConnected ? (
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: 'white',
                      animation: 'chipPulse 1.5s ease-in-out infinite',
                      '@keyframes chipPulse': {
                        '0%': { opacity: 1 },
                        '50%': { opacity: 0.6 },
                        '100%': { opacity: 1 },
                      },
                    }}
                  />
                ) : (
                  <SignalCellularOffIcon sx={{ fontSize: 14 }} />
                )
              }
              label={isConnected ? 'Ao vivo' : 'Offline'}
              size="small"
              sx={{
                bgcolor: isConnected ? 'success.main' : 'error.main',
                color: 'white',
                height: 20,
                fontSize: '0.7rem',
              }}
            />

            {/* Ações: Marcar todas como lidas + Filtro */}
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              {/* Botão Marcar todas como lidas */}
              {unreadCount > 0 && (
                <Tooltip title="Marcar todas como lidas">
                  <IconButton
                    size="small"
                    onClick={handleMarkAllAsRead}
                    sx={{
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
                    }}
                  >
                    <DoneAllIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {/* Toggle Filtro */}
              <ToggleButtonGroup
                value={filter}
                exclusive
                onChange={(_e, newFilter) => {
                  if (newFilter !== null) {
                    setFilter(newFilter);
                  }
                }}
                sx={{
                  height: 24,
                  '& .MuiToggleButton-root': {
                    padding: '4px 8px',
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    color: 'white',
                    '&.Mui-selected': {
                      bgcolor: 'rgba(255, 255, 255, 0.3)',
                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.4)' },
                    },
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
                  },
                }}
              >
                <ToggleButton value="all" aria-label="todas">
                  Todas
                </ToggleButton>
                <ToggleButton value="unread" aria-label="nao-lidas">
                  Não lidas
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
        </Box>

        {/* Corpo scrollavel */}
        <Box
          sx={{
            flexGrow: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Banner: Convidar usuario a ativar push (estado default) */}
          {showDefaultBanner && (
            <Box
              sx={{
                px: 2,
                py: 1.5,
                bgcolor: 'info.lighter',
                background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)',
                borderBottom: '1px solid',
                borderColor: 'divider',
                flexShrink: 0,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <NotificationsActiveIcon sx={{ color: 'primary.main', mt: 0.3, fontSize: 20 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight="bold" color="text.primary">
                    Fique por dentro dos seus agendamentos!
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3 }}>
                    Receba alertas em tempo real, mesmo com o navegador fechado.
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                size="small"
                fullWidth
                onClick={handleActivatePush}
                disabled={pushLoading}
                sx={{ mt: 1, textTransform: 'none', fontWeight: 'bold' }}
              >
                {pushLoading ? 'Ativando...' : 'Ativar notificacoes'}
              </Button>
            </Box>
          )}

          {/* Banner: Usuario bloqueou notificacoes (estado denied) */}
          {showDeniedBanner && (
            <Alert
              severity="warning"
              sx={{
                borderRadius: 0,
                '& .MuiAlert-message': { width: '100%' },
                flexShrink: 0,
              }}
              action={
                <Button color="inherit" size="small" onClick={handleDismissDenied}>
                  Entendi
                </Button>
              }
            >
              <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                Notificacoes bloqueadas
              </Typography>
              <Typography variant="caption">
                Para reativar, clique no icone de cadeado na barra de endereco do navegador e permita notificacoes.
              </Typography>
            </Alert>
          )}

          {showDefaultBanner || showDeniedBanner ? <Divider sx={{ flexShrink: 0 }} /> : null}

          {loading ? (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', flexGrow: 1, alignItems: 'center' }}>
              <CircularProgress />
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center', flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
              <NotificationsNoneIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography color="text.secondary">
                Nenhuma notificacao
              </Typography>
            </Box>
          ) : notifications.filter((n) => filter === 'all' || !n.readAt).length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center', flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
              <NotificationsNoneIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography color="text.secondary">
                Nenhuma notificacao nao lida
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
              <List sx={{ p: 0, m: 0, flex: 1, overflow: 'visible' }}>
                {notifications
                  .filter((n) => filter === 'all' || !n.readAt)
                  .map((notification, index, filteredArray) => {
                    const isUnread = !notification.readAt;
                    const isLastItem = index === filteredArray.length - 1;

                    return (
                      <Box key={notification.id} ref={isLastItem ? observerRef : null}>
                      {index > 0 && <Divider sx={{ opacity: 0.5 }} />}
                      <ListItem
                        disablePadding
                        secondaryAction={
                          isUnread && (
                            <Tooltip title="Marcar como lida">
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={(e) => handleMarkAsRead(notification.id, e)}
                                sx={{
                                  color: 'primary.main',
                                  '&:hover': {
                                    bgcolor: 'primary.50',
                                    borderRadius: '50%',
                                  },
                                }}
                              >
                                <DoneAllIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )
                        }
                      >
                        <ListItemButton
                          onClick={() => handleNotificationClick(notification)}
                          sx={{
                            py: 1,
                            px: 2,
                            display: 'flex',
                            gap: 1.5,
                            alignItems: 'flex-start',
                            bgcolor: isUnread ? 'rgba(25, 118, 210, 0.06)' : 'transparent',
                            borderLeft: '3px solid',
                            borderLeftColor: isUnread ? 'primary.main' : 'transparent',
                            opacity: isUnread ? 1 : 0.75,
                            cursor: (notification.hasBody || notification.actionUrl || notification.data?.actionUrl) ? 'pointer' : 'default',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              bgcolor: isUnread ? 'rgba(25, 118, 210, 0.12)' : 'action.hover',
                            },
                          }}
                        >
                          {/* Avatar com ícone do tipo */}
                          {(() => {
                            const typeInfo = getNotificationTypeInfo(notification.type);
                            const IconComponent = typeInfo.icon;
                            return (
                              <Avatar
                                sx={{
                                  width: 32,
                                  height: 32,
                                  bgcolor: typeInfo.bgColor,
                                  color: typeInfo.color,
                                  fontSize: '0.9rem',
                                  flexShrink: 0,
                                }}
                              >
                                <IconComponent sx={{ fontSize: 18 }} />
                              </Avatar>
                            );
                          })()}

                          <ListItemText
                            sx={{ my: 0 }}
                            primary={
                              <Typography
                                variant="body2"
                                fontWeight={isUnread ? 700 : 400}
                                color={isUnread ? 'text.primary' : 'text.secondary'}
                                sx={{
                                  transition: 'all 0.3s ease',
                                  fontSize: '0.85rem',
                                }}
                              >
                                {notification.title || 'Notificacao'}
                              </Typography>
                            }
                            secondary={
                              <Box>
                                <Typography
                                  variant="body2"
                                  color={isUnread ? 'text.secondary' : 'text.disabled'}
                                  sx={{
                                    mt: 0.3,
                                    transition: 'all 0.3s ease',
                                    fontSize: '0.78rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                  }}
                                >
                                  {notification.summary || notification.message || 'Voce tem uma nova notificacao'}
                                </Typography>

                                {/* Chip de tipo */}
                                {notification.type && (
                                  <Chip
                                    label={getNotificationTypeInfo(notification.type).label}
                                    size="small"
                                    sx={{
                                      mt: 0.5,
                                      height: 16,
                                      fontSize: '0.6rem',
                                      bgcolor: getNotificationTypeInfo(notification.type).bgColor,
                                      color: getNotificationTypeInfo(notification.type).color,
                                      fontWeight: 600,
                                      '& .MuiChip-label': {
                                        px: 0.75,
                                      },
                                    }}
                                  />
                                )}

                                {notification.hasBody && (
                                  <Chip
                                    icon={<ArticleIcon sx={{ fontSize: 14 }} />}
                                    label="Ver detalhes"
                                    size="small"
                                    variant="outlined"
                                    sx={{ ml: 0.5, height: 16, fontSize: '0.6rem' }}
                                  />
                                )}

                                <Typography
                                  variant="caption"
                                  color={isUnread ? 'primary.main' : 'text.disabled'}
                                  sx={{
                                    mt: 0.3,
                                    display: 'block',
                                    transition: 'all 0.3s ease',
                                    fontSize: '0.65rem',
                                  }}
                                >
                                  {formatDate(notification.createdAt)}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    </Box>
                  );
              })}
              </List>

              {/* Loading mais notificacoes */}
              {loadingMore && (
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    Carregando...
                  </Typography>
                </Box>
              )}

              {/* Fim da lista */}
              {!hasMore && notifications.filter((n) => filter === 'all' || !n.readAt).length > 0 && (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                    ✓ Todas as notificacoes carregadas
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Footer fixo */}
        {notifications.length > 0 && (
          <Box
            sx={{
              p: 1,
              textAlign: 'center',
              borderTop: '1px solid',
              borderColor: 'divider',
              flexShrink: 0,
            }}
          >
            <Button size="small" onClick={handleClose}>
              Fechar
            </Button>
          </Box>
        )}
      </Popover>

      <NotificationDetailModal
        open={detailModalOpen}
        onClose={() => {
          setDetailModalOpen(false);
          setDetailNotificationId(null);
        }}
        notificationId={detailNotificationId}
      />
    </>
  );
}
